import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  Award, Camera, CheckCircle2, Compass, Filter,
  Image as ImageIcon, MapPin, Map as MapIcon,
  Navigation, PartyPopper, Star, TrendingUp, X,
} from "lucide-react";

// Take a Tour — a 3-step camper onboarding walkthrough that opens as a
// centered popup on desktop and a full-screen sheet on mobile / PWA.
// Layout is 1:1 with the mobile app screenshots the user provided:
//   • step chip pill at the top ("Step 1 of 3")
//   • large otter illustration on a step-tinted background
//   • title with emoji + short subtitle
//   • white bullet card with 3 rows (icon + label)
//   • bottom: 3-dot indicator + primary CTA
// Colours match the mobile app step tints (mint / warm-yellow / peach)
// and the primary CTA uses the forest-green brand.
export default function TakeTourModal({ open, onClose, firstName = "Camper" }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  // Lock body scroll while the sheet is open so the tour feels modal
  // even on iOS PWA where rubber-band scroll can leak through.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const s = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const StepIcon = s.stepIcon;

  const modal = (
    <div
      className="fixed inset-0 z-[100] flex items-stretch md:items-center md:justify-center md:p-6"
      style={{ background: "rgba(20, 26, 22, 0.55)" }}
      role="dialog"
      aria-modal="true"
      aria-label="Take a Tour"
      data-testid="take-tour-modal"
    >
      <div
        className="relative w-full md:max-w-md md:rounded-3xl md:overflow-hidden flex flex-col md:h-[720px] md:max-h-[92vh]"
        style={{ background: "var(--surface)" }}
      >
        {/* Top action row — sits above the tinted illustration panel */}
        <div className="flex items-center justify-between px-4 pt-4 pb-3 shrink-0">
          <button
            onClick={onClose}
            aria-label="Close tour"
            data-testid="tour-close-btn"
            className="w-10 h-10 rounded-full flex items-center justify-center transition"
            style={{ background: "var(--card)", boxShadow: "0 1px 2px rgba(0,0,0,0.06)" }}
          >
            <X className="w-5 h-5" style={{ color: "var(--on-surface)" }} />
          </button>
          {!isLast && (
            <button
              onClick={onClose}
              data-testid="tour-skip-btn"
              className="px-4 h-10 rounded-full text-sm font-extrabold transition"
              style={{ background: "var(--card)", color: "var(--on-surface-muted)",
                       boxShadow: "0 1px 2px rgba(0,0,0,0.06)" }}
            >
              Skip
            </button>
          )}
        </div>

        {/* Tinted illustration + title area — this is what changes colour
            with each step to match the mobile app's mood tints. */}
        <div
          className="flex-1 relative overflow-y-auto flex flex-col items-center px-6 pt-4 pb-6"
          style={{ background: s.bg }}
        >
          {/* step chip */}
          <div
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-extrabold"
            style={{ background: "var(--card)", color: "var(--brand)" }}
            data-testid={`tour-step-chip-${step}`}
          >
            <StepIcon className="w-4 h-4" />
            Step {step + 1} of {STEPS.length}
          </div>

          {/* illustration */}
          <img
            src={s.image}
            alt=""
            className="w-full max-w-[300px] object-contain mt-4"
            style={{ height: 260 }}
            data-testid={`tour-illustration-${step}`}
          />

          <h2
            className="text-2xl md:text-3xl font-extrabold text-center mt-3"
            style={{ color: "var(--brand)" }}
            data-testid={`tour-title-${step}`}
          >
            {s.title} <span aria-hidden>{s.titleEmoji}</span>
          </h2>
          <p
            className="text-sm md:text-base text-center mt-1"
            style={{ color: "var(--on-surface-muted)" }}
          >
            {s.subtitle}
          </p>

          {/* Bullet card */}
          <div
            className="w-full mt-5 rounded-2xl p-4 flex flex-col gap-3"
            style={{ background: "var(--card)" }}
          >
            {s.bullets.map((b, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`tour-bullet-${step}-${i}`}>
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: "color-mix(in srgb, var(--brand) 12%, transparent)", color: "var(--brand)" }}
                >
                  <b.icon className="w-4 h-4" />
                </div>
                <div className="text-sm font-bold" style={{ color: "var(--on-surface)" }}>
                  {b.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer — dot indicator + primary CTA. Sits outside the
            tinted panel so it stays anchored on iOS PWA. */}
        <div
          className="px-6 pt-3 pb-6 shrink-0 flex flex-col items-center gap-3"
          style={{ background: "var(--surface)", paddingBottom: "max(24px, env(safe-area-inset-bottom))" }}
        >
          <div className="flex items-center gap-1.5" data-testid="tour-dots">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className="rounded-full transition-all"
                style={{
                  width: i === step ? 22 : 8,
                  height: 8,
                  background: i === step ? "var(--brand)" : "color-mix(in srgb, var(--brand) 25%, transparent)",
                }}
              />
            ))}
          </div>
          <button
            onClick={() => (isLast ? onClose() : setStep((v) => v + 1))}
            className="w-full h-12 rounded-full text-base font-extrabold inline-flex items-center justify-center gap-2 active:scale-[0.99] transition"
            style={{ background: "var(--brand-deep)", color: "white" }}
            data-testid={isLast ? "tour-finish-btn" : "tour-next-btn"}
          >
            {isLast ? (
              <>Let&apos;s Go, {firstName}! <PartyPopper className="w-4 h-4" /> <CheckCircle2 className="w-4 h-4" /></>
            ) : (
              <>Next <span aria-hidden>→</span></>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

const STEPS = [
  {
    stepIcon: MapIcon,
    image: "/assets/tour/step-1.webp",
    bg: "#d5ebd8",
    title: "Explore Campsites",
    titleEmoji: "🌲",
    subtitle: "Find your next adventure on the map!",
    bullets: [
      { icon: MapPin, label: "Browse verified campsites near you" },
      { icon: Filter, label: "Filter by state, facilities & ratings" },
      { icon: Compass, label: "Tap any pin on the map for full details" },
    ],
  },
  {
    stepIcon: CheckCircle2,
    image: "/assets/tour/step-2.webp",
    bg: "#f7ebc4",
    title: "Collect Stamps",
    titleEmoji: "🎯",
    subtitle: "Every stay earns a unique digital stamp!",
    bullets: [
      { icon: Camera, label: "Show your QR code at check-in" },
      { icon: Navigation, label: "Or GPS-check-in when you're on-site" },
      { icon: TrendingUp, label: "Every stamp levels up your camper rank" },
    ],
  },
  {
    stepIcon: Award,
    image: "/assets/tour/step-3.webp",
    bg: "#f3dcc6",
    title: "Build Your Passport",
    titleEmoji: "🏆",
    subtitle: "Your journey, stamped and celebrated.",
    bullets: [
      { icon: ImageIcon, label: "Attach photos & journals to each memory" },
      { icon: Star, label: "Rate campsites and share the love" },
      { icon: Award, label: "Unlock 16+ milestone achievement badges" },
    ],
  },
];
